// design-system-exempt: same as BrandingPanel — the swatch list is a set of choices
// offered to whoever is naming the deployment, not the page's own palette.
// First run. Shown once, when a fresh deployment has not been named yet.
//
// Its whole job is to get past the moment where the product is called "Crum" on
// someone else's domain. Everything here is also in Settings → Branding, so this
// screen is a prompt rather than a separate configuration surface — and it can be
// skipped, because a deployment with no name still works.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Brand as Mark } from '../components/Mark'
import { type Brand } from '../lib/brand'
import { useStore } from '../lib/store'
import { applyBrand } from '../lib/theme'

// Ember and marine first: they are the product's own two, and the rest are
// warm-leaning alternatives rather than a generic swatch row.
const SUGGESTED = ['#a8501f', '#1d5f6a', '#8a6a24', '#46734a', '#a03c33', '#5f3dc4', '#343a40']

export function SetupPage() {
  const { brand, saveBrand, admin } = useStore()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<Brand>(brand)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function finish() {
    if (!draft.instanceName.trim()) return
    setBusy(true)
    setError('')
    try {
      await saveBrand(draft)
      applyBrand(draft)
      navigate('/overview', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.')
      setBusy(false)
    }
  }

  return (
    <div
      className="flex min-h-dvh items-center justify-center px-6"
      style={{ background: 'var(--bg)', color: 'var(--fg)' }}
    >
      <div className="w-full max-w-[26rem]">
        <div className="mb-5 flex items-center gap-2.5">
          <Mark size={24} />
          <h1 className="text-title font-semibold tracking-tight">Name this deployment</h1>
        </div>
        <p className="text-body mb-5 leading-relaxed" style={{ color: 'var(--muted)' }}>
          This instance is yours. What it is called, and what colour it uses, are stored with your
          data rather than in the deployment, so you can change them any time in Settings.
        </p>

        {!admin ? (
          <p className="text-body" style={{ color: 'var(--muted)' }}>
            An administrator needs to set this up. You can{' '}
            <button
              type="button"
              onClick={() => navigate('/overview', { replace: true })}
              className="underline"
            >
              carry on without it
            </button>
            .
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="meta">Name</span>
              <input
                autoFocus
                value={draft.instanceName}
                onChange={(e) => setDraft({ ...draft, instanceName: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && void finish()}
                placeholder="Knowledge Futures Projects"
                className="rounded-control text-body border px-3 py-2 outline-none"
                style={{
                  borderColor: 'var(--border)',
                  background: 'var(--panel)',
                  color: 'var(--fg)',
                }}
              />
            </label>

            <div className="flex flex-col gap-1">
              <span className="meta">Accent</span>
              <div className="flex flex-wrap items-center gap-2">
                {SUGGESTED.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    title={hex}
                    onClick={() => setDraft({ ...draft, accent: hex })}
                    className="h-7 w-7 rounded-full"
                    style={{
                      background: hex,
                      outline: draft.accent === hex ? `2px solid ${hex}` : 'none',
                      outlineOffset: 2,
                    }}
                  />
                ))}
                <input
                  type="color"
                  value={draft.accent}
                  onChange={(e) => setDraft({ ...draft, accent: e.target.value })}
                  className="rounded-inline h-7 w-10 cursor-pointer border bg-transparent"
                  style={{ borderColor: 'var(--border)' }}
                />
              </div>
            </div>

            <button
              type="button"
              disabled={busy || !draft.instanceName.trim()}
              onClick={() => void finish()}
              className="rounded-control text-body mt-1 px-3 py-2 font-medium disabled:opacity-50"
              // The accent is not saved yet, so there is no --accent-contrast to read:
              // this is a live preview of a colour the workspace has not adopted.
              style={{ background: draft.accent, color: '#fff' }} // design-system-exempt
            >
              {busy ? 'Saving…' : 'Start using it'}
            </button>
            {error ? (
              <p className="text-meta" style={{ color: 'var(--crit)' }}>
                {error}
              </p>
            ) : null}
            {/* A logo is the one thing worth doing later — it needs a file to hand. */}
            <button
              type="button"
              onClick={() => navigate('/overview', { replace: true })}
              className="meta underline"
            >
              skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
